/*
 * -----------------------------------------------------------------------
 *  <copyright file="item-list.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { List, ListItem, ListItemSecondaryAction, ListItemText } from '@mui/material';
import { Checkbox } from '@progress/kendo-react-inputs';
import React, { useState, useEffect } from 'react';

import { useBoolean, useTranslation } from '../../../hooks';
import { showMaxLength, showWithFilter } from '../../../utils/filter';
import { MenuButton } from './buttons';

import './item-list.scss';
import Badge from '@components/core/badge';

interface IItemListParams {
    id?: string;
    title?: string;
    items: any[];
    textField?: string;
    menuItems?: any[];
    style?: any;
    itemStyle?: any;
    listStyle?: any;
    multiselect?: boolean;
    onSelectionChange?: any;
    filter?: any;
    showCheckboxes?: boolean;
    selectedItems?: any;
    onIconClick?: any;
    closed?: boolean;
    fields?: string[];
    showStudyReadOnly?: boolean;
}

const ItemList = ({
    id,
    title,
    items,
    textField,
    menuItems,
    itemStyle,
    style,
    listStyle,
    multiselect,
    onSelectionChange,
    filter,
    selectedItems,
    showCheckboxes = true,
    onIconClick,
    closed,
    fields,
    showStudyReadOnly = false,
}: IItemListParams) => {
    const { t } = useTranslation();

    const [selectedIndexes, setSelectedIndexes] = useState([]);

    useEffect(() => {
        if (items && items.length && selectedIndexes.length) {
            const array = selectedIndexes.filter(
                (item: any) => items.findIndex((element: any) => element.id === item.id) >= 0,
            );
            setSelectedIndexes(array || []);
        }
    }, []);

    useEffect(() => {
        setSelectedIndexes(selectedItems || []);
    }, [selectedItems]);

    useEffect(() => {
        if (closed) setSelectedIndexes([]);
    }, [closed]);

    return (
        <div
            id={id}
            style={{ width: '100%', ...style }}
            className="opra-item-list"
            data-testid={id ?? 'opra-item-list'}
        >
            {title ? (
                <h3 className="secondary" style={{ marginTop: 0, marginBottom: 0 }}>
                    {title}
                </h3>
            ) : null}
            <div style={listStyle}>
                <List>
                    {(items || []).map((item: any, index: number) => {
                        if (!item) return null;

                        const isSelected = selectedIndexes.findIndex((element: any) => item?.id === element?.id) >= 0;

                        return (
                            <ListItem
                                key={index}
                                dense
                                button
                                disableRipple
                                selected={isSelected}
                                className="list-item-dialog"
                                style={index % 2 ? { backgroundColor: '#fafafa' } : null}
                                onClick={() => {
                                    if (!item.disabled) {
                                        let indexes = [...selectedIndexes];
                                        const i = indexes.findIndex((element: any) => item?.id === element?.id);

                                        if (i >= 0) indexes.splice(i, 1);
                                        else if (multiselect) indexes.push(item);
                                        else indexes = [i];
                                        setSelectedIndexes(indexes);
                                        onSelectionChange(indexes);
                                    }
                                }}
                                data-testid={`list-item-${index}`}
                            >
                                <ListItemText className="list-item-text-item">
                                    {showCheckboxes ? (
                                        <Checkbox
                                            rounded={null}
                                            className="cell-checkbox mr no-full-width add-risk"
                                            checked={isSelected}
                                            disabled={item.disabled}
                                            size="medium"
                                        />
                                    ) : null}
                                    {fields?.length ? (
                                        <span
                                            style={itemStyle}
                                            className={isSelected ? 'items-list selected' : 'items-list'}
                                        >
                                            {fields.map((element: any) => (
                                                <span className="item-list">
                                                    {showMaxLength(
                                                        filter && filter.length
                                                            ? showWithFilter(element ? item[element] : item, filter)
                                                            : element
                                                              ? item[element]
                                                              : item,
                                                        200,
                                                    )}
                                                </span>
                                            ))}
                                        </span>
                                    ) : null}
                                    {!fields?.length ? (
                                        <span
                                            style={itemStyle}
                                            className={isSelected ? 'item-list selected' : 'item-list'}
                                        >
                                            {showMaxLength(
                                                filter && filter.length
                                                    ? showWithFilter(textField ? item[textField] : item, filter)
                                                    : textField
                                                      ? item[textField]
                                                      : item,
                                                200,
                                            )}
                                        </span>
                                    ) : null}

                                    {showStudyReadOnly && item?.studyReadOnly === true && (
                                        <div style={{ position: 'absolute', right: 50, pointerEvents: 'none' }}>
                                            <Badge>{t('common:readOnly')}</Badge>
                                        </div>
                                    )}
                                </ListItemText>

                                {menuItems && menuItems.length && (
                                    <ListItemSecondaryAction>
                                        <MenuButton icon={['fal', 'ellipsis-v']} items={menuItems} item={item} />
                                    </ListItemSecondaryAction>
                                )}

                                {(item.icon || item.isLinkIcon) && (
                                    <ListItemSecondaryAction
                                        className={isSelected ? 'item-icon selected' : 'item-icon'}
                                    >
                                        {item.isLinkIcon ? (
                                            <FontAwesomeIcon style={{ marginRight: 5 }} icon={['fas', 'link']} />
                                        ) : null}
                                        {item.icon ? (
                                            <FontAwesomeIcon
                                                icon={[item.fal ? 'fal' : 'fas', item.icon]}
                                                onClick={() => {
                                                    if (onIconClick) {
                                                        item.fal = !item.fal;
                                                        onIconClick(item);
                                                    }
                                                }}
                                            />
                                        ) : null}
                                    </ListItemSecondaryAction>
                                )}
                            </ListItem>
                        );
                    })}
                </List>
            </div>
        </div>
    );
};

export default ItemList;
